/**
 * EzoicSlot — Real Ezoic display ad slot (standalone `ezstandalone` JS), env-gated.
 *
 * Activation: set these in /app/frontend/.env (values come from your Ezoic
 * dashboard AFTER your site is approved):
 *   REACT_APP_EZOIC_SITE_ID            = your Ezoic site id
 *   REACT_APP_EZOIC_INTERSTITIAL_ID    = numeric placeholder id for the popup slot
 *   REACT_APP_EZOIC_BANNER_ID          = numeric placeholder id for inline banners (optional)
 *   REACT_APP_EZOIC_SCRIPT_URL         = (optional) override the sa.min.js URL
 *
 * Until those are set, isEzoicConfigured() is false → the caller renders the
 * simulated house creative instead (so the app is fully testable today, and
 * flips to real Ezoic ads the moment you paste the IDs — no code change).
 *
 * NOTE: Ezoic does NOT provide sandbox/test ad units for un-approved sites
 * (unlike AdMob's TestIds). Real ads only render once your account + site are
 * live. The house creative is the local/preview "test" experience.
 */
import { useEffect, useRef } from 'react';

const DEFAULT_SCRIPT = 'https://www.ezojs.com/ezoic/sa.min.js';
let scriptLoadedPromise = null;

function ezoicConfig() {
  return {
    siteId: process.env.REACT_APP_EZOIC_SITE_ID,
    scriptUrl: process.env.REACT_APP_EZOIC_SCRIPT_URL || DEFAULT_SCRIPT,
    interstitialId: Number(process.env.REACT_APP_EZOIC_INTERSTITIAL_ID || '0'),
    bannerId: Number(process.env.REACT_APP_EZOIC_BANNER_ID || '0'),
  };
}

/** True if Ezoic has at least a site id + the interstitial placeholder set. */
export function isEzoicConfigured() {
  const { siteId, interstitialId } = ezoicConfig();
  return Boolean(siteId && interstitialId > 0);
}

/** True if a usable Ezoic banner placeholder id is configured. */
export function isEzoicBannerConfigured() {
  const { siteId, bannerId } = ezoicConfig();
  return Boolean(siteId && bannerId > 0);
}

function loadEzoicScript(scriptUrl) {
  if (scriptLoadedPromise) return scriptLoadedPromise;
  scriptLoadedPromise = new Promise((resolve, reject) => {
    if (typeof window === 'undefined') return reject('no-window');
    if (!window.ezstandalone) window.ezstandalone = { cmd: [] };
    if (document.querySelector('script[data-emergent-ezoic]')) return resolve();
    const s = document.createElement('script');
    s.async = true;
    s.dataset.emergentEzoic = '1';
    s.src = scriptUrl;
    s.onload = resolve;
    s.onerror = () => { scriptLoadedPromise = null; reject('script-load-failed'); };
    document.head.appendChild(s);
  });
  return scriptLoadedPromise;
}

/**
 * Renders a single Ezoic placeholder. `placeholderId` defaults to the
 * interstitial id; pass the banner id for inline placements.
 */
export default function EzoicSlot({ placeholderId, minHeight = 250, testId = 'ezoic-slot' }) {
  const { scriptUrl, interstitialId } = ezoicConfig();
  const id = Number(placeholderId || interstitialId || 0);
  const { siteId } = ezoicConfig();
  const configured = Boolean(siteId && id > 0);
  const ref = useRef(null);

  useEffect(() => {
    if (!configured) return;
    let cancelled = false;
    loadEzoicScript(scriptUrl)
      .then(() => {
        if (cancelled) return;
        window.ezstandalone = window.ezstandalone || { cmd: [] };
        window.ezstandalone.cmd.push(() => {
          try { window.ezstandalone.showAds(id); }
          catch (e) { console.warn('[ezoic] showAds failed:', e); }
        });
      })
      .catch((e) => console.warn('[ezoic] script failed:', e));
    return () => {
      cancelled = true;
      // Best-effort SPA cleanup so route/tab changes don't leak the placeholder.
      try {
        if (window.ezstandalone && typeof window.ezstandalone.destroyPlaceholders === 'function') {
          window.ezstandalone.cmd.push(() => window.ezstandalone.destroyPlaceholders(id));
        }
      } catch (_e) { /* noop */ }
    };
  }, [configured, scriptUrl, id]);

  if (!configured) return null;

  return (
    <div
      ref={ref}
      id={`ezoic-pub-ad-placeholder-${id}`}
      style={{ display: 'block', width: '100%', minHeight }}
      data-testid={testId}
    />
  );
}
